import React, { useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Button,
  CardMedia,
  FormLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import MovieCreationOutlinedIcon from '@mui/icons-material/MovieCreationOutlined';
import ClearIcon from '@mui/icons-material/HighlightOff';
import TemplatesApi from './Templates.api';
import styleColors from '../colors.styles';
import adminFormStyles from '../shared/styles/adminForm.styles';
import associateSpark from '../assets/associate-spark.png';
import SpinningSpark from '../shared/components/SpinningSpark';
import { AlertsContext } from '../shared/alerts/Alerts.context';
import { SHOPPABLE_CATEGORY_ID } from './Template.model';

const MAX_IMAGE_SIZE_IN_MB = 8;
const MAX_VIDEO_SIZE_IN_MB = 500;

const DASHED_BORDER = `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%23c5cad3' stroke-width='2' stroke-dasharray='6%2c 10' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`;

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const styles = {
  field: adminFormStyles.field,
  fieldLabel: adminFormStyles.fieldLabel,
  mediaPicker: {
    backgroundColor: styleColors.base.light,
    backgroundImage: DASHED_BORDER,
    borderRadius: '8px',
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    minHeight: 140,
    overflow: 'hidden',
  },
  mediaZone: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: 0.75,
    justifyContent: 'center',
    minHeight: 140,
    p: 2,
    textAlign: 'center',
    transition: 'background-color 120ms ease, color 120ms ease',
    '&:hover': {
      backgroundColor: styleColors.base.barely_blue,
      '& .media-icon-circle': {
        backgroundColor: '#e8f1fd',
      },
      '& .media-icon': {
        color: styleColors.base.walmart_blue,
      },
      '& .media-title': {
        color: styleColors.base.walmart_blue,
        fontWeight: 700,
      },
      '& .media-subtitle': {
        color: styleColors.base.walmart_blue,
      },
    },
  },
  mediaZoneDivider: {
    backgroundColor: '#c5cad3',
    display: { xs: 'block', sm: 'none' },
    flexShrink: 0,
    height: '1px',
    width: '100%',
  },
  mediaZoneDividerVertical: {
    backgroundColor: '#c5cad3',
    display: { xs: 'none', sm: 'block' },
    flexShrink: 0,
    width: '1px',
  },
  mediaIconCircle: {
    alignItems: 'center',
    backgroundColor: '#eef1f4',
    borderRadius: '50%',
    display: 'flex',
    height: 40,
    justifyContent: 'center',
    transition: 'background-color 120ms ease',
    width: 40,
  },
  mediaIcon: {
    color: '#8b93a5',
    fontSize: 22,
    transition: 'color 120ms ease',
  },
  mediaTitle: {
    color: '#4b5565',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: 1.3,
    transition: 'color 120ms ease, font-weight 120ms ease',
  },
  mediaSubtitle: {
    color: '#8b93a5',
    fontSize: '12px',
    lineHeight: 1.3,
    maxWidth: 220,
    transition: 'color 120ms ease',
  },
  mediaPreview: {
    borderRadius: '8px',
    maxWidth: 360,
    width: '100%',
  },
  messageField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#f6f9fc',
      borderRadius: '8px',
      fontSize: '14px',
      '& fieldset': {
        borderColor: '#dbe2e6',
      },
      '&:hover fieldset': {
        borderColor: '#a7b1c0',
      },
      '&.Mui-focused fieldset': {
        borderColor: styleColors.base.walmart_blue,
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '12px 16px',
    },
  },
  pageName: {
    color: '#041f41',
    fontSize: '14px',
    fontWeight: 700,
  },
};

function MediaPickerZone({
  title,
  subtitle,
  icon,
  onClick,
  component,
  children,
}) {
  return (
    <Box
      component={component}
      onClick={onClick}
      role={component === 'label' ? undefined : 'button'}
      tabIndex={component === 'label' ? undefined : 0}
      onKeyDown={
        onClick
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick(event);
              }
            }
          : undefined
      }
      sx={styles.mediaZone}
    >
      <Box className="media-icon-circle" sx={styles.mediaIconCircle}>
        {icon}
      </Box>
      <Typography className="media-title" sx={styles.mediaTitle}>
        {title}
      </Typography>
      <Typography className="media-subtitle" sx={styles.mediaSubtitle}>
        {subtitle}
      </Typography>
      {children}
    </Box>
  );
}

MediaPickerZone.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  component: PropTypes.string,
  children: PropTypes.node,
};

MediaPickerZone.defaultProps = {
  onClick: undefined,
  component: 'div',
  children: null,
};

export default function TemplateContentForm({
  template,
  onChange,
  renderError,
}) {
  const { showAlert } = useContext(AlertsContext);
  const [loading, setLoading] = useState(false);
  const textFieldRef = useRef(null);
  const isShoppableTemplate = template.category === SHOPPABLE_CATEGORY_ID;

  async function uploadMedia(file) {
    if (!file) return;

    const isImage = file.type.startsWith('image');
    const maxFileSize = isImage ? MAX_IMAGE_SIZE_IN_MB : MAX_VIDEO_SIZE_IN_MB;

    if (file.size > maxFileSize * 1024 * 1024) {
      showAlert(
        `${
          isImage ? 'Images' : 'Videos'
        } must be smaller than ${maxFileSize} MB`,
        'error'
      );
      return;
    }

    setLoading(true);
    try {
      const fileUrl = await TemplatesApi.uploadTemplateMedia(file);
      onChange((currentTemplate) => ({
        ...currentTemplate,
        source: fileUrl,
        type: isImage ? 'photo' : 'video',
      }));
    } catch (err) {
      console.error(`Failed to upload media: ${err}`);
      showAlert('Failed to upload media', 'error');
    } finally {
      setLoading(false);
    }
  }

  const fileAccept = isShoppableTemplate
    ? '.png,.jpg,.jpeg'
    : '.png,.jpg,.mp4,.mov,.jpeg';

  function renderMediaSection() {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" py={3}>
          <SpinningSpark marginTop="0" />
        </Box>
      );
    }

    if (template.source) {
      return (
        <Box position="relative" sx={styles.mediaPreview}>
          <CardMedia
            src={template.source}
            component={
              template.type === 'photo' || isShoppableTemplate ? 'img' : 'video'
            }
            controls
            sx={{ borderRadius: '8px' }}
          />
          <IconButton
            onClick={() => {
              onChange((currentTemplate) => ({
                ...currentTemplate,
                source: null,
                type: 'text',
              }));
            }}
            sx={{
              position: 'absolute',
              padding: 0,
              top: -10,
              left: -10,
            }}
          >
            <ClearIcon
              titleAccess="Remove media"
              sx={{
                width: 25,
                height: 25,
                borderRadius: '50%',
                backgroundColor: 'white',
              }}
            />
          </IconButton>
        </Box>
      );
    }

    const uploadTitle = isShoppableTemplate
      ? 'Upload image'
      : 'Upload image or video';
    const uploadSubtitle = isShoppableTemplate ? 'PNG, JPG' : 'PNG, JPG, MP4';

    const uploadZone = (
      <MediaPickerZone
        component="label"
        title={uploadTitle}
        subtitle={uploadSubtitle}
        icon={
          <CloudUploadOutlinedIcon
            className="media-icon"
            sx={styles.mediaIcon}
          />
        }
      >
        <VisuallyHiddenInput
          type="file"
          accept={fileAccept}
          onChange={(event) => uploadMedia(event.target.files?.[0])}
        />
      </MediaPickerZone>
    );

    if (isShoppableTemplate) {
      return (
        <Stack spacing={1}>
          <Box sx={styles.mediaPicker}>{uploadZone}</Box>
          {renderError('source')}
        </Stack>
      );
    }

    return (
      <Stack spacing={1}>
        <Box sx={styles.mediaPicker}>
          {uploadZone}
          <Box sx={styles.mediaZoneDivider} />
          <Box sx={styles.mediaZoneDividerVertical} />
          <MediaPickerZone
            title="Create an editable video"
            subtitle="Have users swap clips or change text"
            icon={
              <MovieCreationOutlinedIcon
                className="media-icon"
                sx={styles.mediaIcon}
              />
            }
            onClick={() => {
              // Placeholder until WMTA-10043.
            }}
          />
        </Box>
        {renderError('source')}
      </Stack>
    );
  }

  return (
    <Stack spacing="20px">
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            alt="Spark logo"
            src={associateSpark}
            sx={{ width: 42, height: 42 }}
          />
          <Typography sx={styles.pageName}>Page Name</Typography>
        </Stack>

        {template.category === 'fitb' && (
          <Button
            type="button"
            variant="contained"
            size="small"
            sx={{
              backgroundColor: styleColors.base.walmart_blue,
              textTransform: 'none',
            }}
            onClick={() => {
              const start = textFieldRef.current.selectionStart;
              const end = textFieldRef.current.selectionEnd;

              onChange((currentTemplate) => {
                const firstPart = currentTemplate.message
                  .slice(0, start)
                  .trimEnd();
                const secondPart = currentTemplate.message
                  .slice(end)
                  .trimStart();
                const newMessage = `${firstPart} {blank} ${secondPart}`.trim();

                const blankPosition =
                  firstPart.length +
                  newMessage.slice(firstPart.length).search('{blank}');

                textFieldRef.current.focus();
                setTimeout(() => {
                  textFieldRef.current.setSelectionRange(
                    blankPosition + 1,
                    blankPosition + 6
                  );
                }, 50);

                return { ...currentTemplate, message: newMessage };
              });
            }}
          >
            Insert blank
          </Button>
        )}
      </Stack>

      {renderMediaSection()}

      {isShoppableTemplate && (
        <Stack spacing={0.75}>
          <FormLabel htmlFor="template-product-title" sx={styles.fieldLabel}>
            Product title
          </FormLabel>
          <TextField
            fullWidth
            id="template-product-title"
            variant="outlined"
            value={template.title || ''}
            onChange={(event) => {
              onChange((currentTemplate) => ({
                ...currentTemplate,
                title: event.target.value,
              }));
            }}
            placeholder="Enter product title"
            sx={styles.field}
          />
          {renderError('title')}
        </Stack>
      )}

      {isShoppableTemplate && (
        <Stack spacing={0.75}>
          <FormLabel
            htmlFor="template-product-description"
            sx={styles.fieldLabel}
          >
            Product description
          </FormLabel>
          <TextField
            fullWidth
            id="template-product-description"
            variant="outlined"
            value={template.description || ''}
            onChange={(event) => {
              onChange((currentTemplate) => ({
                ...currentTemplate,
                description: event.target.value,
              }));
            }}
            placeholder="Enter product description"
            multiline
            minRows={2}
            sx={styles.field}
          />
          {renderError('description')}
        </Stack>
      )}

      {isShoppableTemplate && (
        <Stack spacing={0.75}>
          <FormLabel htmlFor="template-product-url" sx={styles.fieldLabel}>
            Product URL
          </FormLabel>
          <TextField
            fullWidth
            id="template-product-url"
            variant="outlined"
            value={template.link || ''}
            onChange={(event) => {
              onChange((currentTemplate) => ({
                ...currentTemplate,
                link: event.target.value,
              }));
            }}
            placeholder="Enter product URL (e.g., https://www.walmart.com/product/12345)"
            sx={styles.field}
          />
          {renderError('link')}
        </Stack>
      )}

      <Stack spacing={0.75}>
        <TextField
          multiline
          fullWidth
          minRows={4}
          inputRef={textFieldRef}
          placeholder="Enter your post content..."
          value={template.message}
          onChange={(event) => {
            onChange((currentTemplate) => ({
              ...currentTemplate,
              message: event.target.value,
            }));
          }}
          sx={styles.messageField}
        />
        {renderError('message')}
      </Stack>
    </Stack>
  );
}

TemplateContentForm.propTypes = {
  template: PropTypes.shape({
    category: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    source: PropTypes.string,
    type: PropTypes.string.isRequired,
    link: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  renderError: PropTypes.func.isRequired,
};
